import { apiUrl } from '../config/environment';

const API_BASE_URL = apiUrl;

// Utility: Fetch with timeout to prevent hanging requests
const fetchWithTimeout = async (url: string, options: RequestInit = {}, timeout = 10000): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if ((error as Error).name === 'AbortError') {
      throw new Error(`Request timeout after ${timeout}ms`);
    }
    throw error;
  }
};

// Utility: Retry failed requests with exponential backoff
const fetchWithRetry = async (
  url: string,
  options: RequestInit = {},
  maxRetries = 2,
  timeout = 10000
): Promise<Response> => {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetchWithTimeout(url, options, timeout);

      // Don't retry on client errors (4xx), only server errors (5xx) and network issues
      if (response.ok || (response.status >= 400 && response.status < 500)) {
        return response;
      }

      // Server error - retry if we have attempts left
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 3000); // Max 3s delay
        console.warn(`API request failed (${response.status}), retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      return response;
    } catch (error) {
      lastError = error as Error;

      // Retry on network errors if we have attempts left
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 3000);
        console.warn(`API request failed (${lastError.message}), retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
    }
  }

  throw lastError || new Error('Request failed after retries');
};

export interface User {
  id: string;
  name: string;
  email: string;
  username?: string;      // Display name (can be any characters, emojis, etc.)
  url_slug?: string;      // URL-safe routing slug (only a-z0-9)
  role?: string;
  created_at: string;
  bio?: string;
  profile_picture?: string;
  avatar_url?: string;
  avatar?: string;
  specialty_sport?: string;
  other_sports?: string;
  social_links?: string;
  phone?: string;
  years_experience?: number;
  price?: number;
  auth_id?: string; // Supabase Auth user ID for security validation
  is_featured?: boolean; // Whether this creator is featured on shop picks page
  total_sales_count?: number; // Total sales count (initial purchases + renewals) for sorting
}

export interface Client {
  id: string;
  name: string;
  contact_email: string;
  created_at: string;
}

export interface Pick {
  id: string;
  user_id: string;
  client_id: string;
  pick_details: string;
  created_at: string;
}

export interface Purchase {
  id: string;
  user_id: string;
  creator_id: string;
  amount: number;
  purchase_date: string;
  status: string;
  creator_name?: string;
  creator_email?: string;
  newBalance?: number;
  is_trial?: boolean;
  trial_start?: string;
  trial_end?: string;
  promo_code_id?: string;
  stripe_promo_code?: string;
}

export interface SMSNotification {
  id: string;
  user_id: string;
  creator_id: string;
  phone_number: string;
  is_active: boolean;
  notification_type: string;
  created_at: string;
  updated_at: string;
  creator_name?: string;
  creator_email?: string;
}

export interface SMSLog {
  id: string;
  user_id: string;
  creator_id: string;
  pick_id: string;
  phone_number: string;
  message: string;
  status: string;
  sent_at: string;
  error_message?: string;
  creator_name?: string;
}

export interface SubscriptionPricing {
  id?: string;
  creator_id: string;
  creator_name?: string;
  creator_email?: string;
  daily_price?: number | null;
  weekly_price?: number | null;
  monthly_price?: number | null;
  yearly_price?: number | null;
  daily_description?: string | null;
  weekly_description?: string | null;
  monthly_description?: string | null;
  yearly_description?: string | null;
  billing_frequency?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  is_active?: boolean;
  is_free?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface DeliveryMethod {
  id: string;
  creator_id: string;
  creator_name?: string;
  creator_email?: string;
  method_type: 'discord' | 'telegram' | 'whatsapp' | 'email' | 'sms' | 'other';
  method_name: string;
  method_value: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SubscriberWithNotifications {
  purchase_id: string;
  user_id: string;
  subscriber_name: string;
  subscriber_email: string;
  subscriber_phone?: string;
  subscription_type: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'free';
  status: 'active' | 'cancelled' | 'expired';
  purchase_date: string;
  end_date?: string;
  amount?: number;
  sms_enabled: boolean;
  email_enabled: boolean;
  sms_phone_number?: string;
  sms_notification_id?: string;
  email_preference_id?: string;
}

export interface Deposit {
  id: number;
  user_id: string;
  user_name?: string;
  user_email?: string;
  amount: number;
  payment_method: string;
  transaction_id: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  previous_balance: number;
  new_balance: number;
  created_at: string;
  updated_at: string;
}

// API functions
export const api = {
  // Register as creator
  registerAsCreator: async (data: {
    userId: string;
    username: string;
    bio: string;
    profilePicture: string;
    specialtySport: string;
    otherSports?: string[];
    socialLinks: any;
    country: string;
  }): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/users/${data.userId}/register-creator`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: data.username,
        bio: data.bio,
        profilePicture: data.profilePicture,
        specialtySport: data.specialtySport,
        otherSports: data.otherSports,
        socialLinks: data.socialLinks,
        country: data.country
      }),
    });
    
    if (!response.ok) {
      let errorMessage = 'Failed to register as creator';
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json();
          errorMessage = error.error || errorMessage;
        } else {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
      } catch (e) {
        errorMessage = `Server error: ${response.status}`;
      }
      throw new Error(errorMessage);
    }
    return response.json();
  },
  
  // Update user profile
  updateUserProfile: async (userId: string, profileData: any): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(profileData),
    });
    if (!response.ok) {
      throw new Error(`Failed to update profile: ${response.statusText}`);
    }
    return response.json();
  },

  // Stripe Connect onboarding methods
  getOnboardingStatus: async (creatorId: string): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/creators/onboarding-status/${creatorId}`);
    if (!response.ok) {
      throw new Error('Failed to get onboarding status');
    }
    return response.json();
  },

  createOnboardingLink: async (creatorId: string, email: string, name: string): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/creators/onboard`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        creatorId,
        email,
        name
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to create onboarding link');
    }
    return response.json();
  },

  getCreatorBalance: async (creatorId: string): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/creators/balance/${creatorId}`);
    if (!response.ok) {
      throw new Error('Failed to get creator balance');
    }
    return response.json();
  },

  requestPayout: async (creatorId: string, amount: number, description?: string): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/creators/payout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        creatorId,
        amount,
        description
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to request payout');
    }
    return response.json();
  },

  getCreatorEarnings: async (creatorId: string, limit = 50, offset = 0): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/creators/earnings/${creatorId}?limit=${limit}&offset=${offset}`);
    if (!response.ok) {
      throw new Error('Failed to get creator earnings');
    }
    return response.json();
  },

  getCreatorPayouts: async (creatorId: string, limit = 20, offset = 0): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/creators/payouts/${creatorId}?limit=${limit}&offset=${offset}`);
    if (!response.ok) {
      throw new Error('Failed to get creator payouts');
    }
    return response.json();
  },

  // Stripe Connect Integration APIs
  getStripeBalance: async (creatorId: string): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/creators/stripe-balance/${creatorId}`);
    if (!response.ok) {
      throw new Error('Failed to get Stripe balance');
    }
    return response.json();
  },

  getStripeRevenue: async (creatorId: string, period?: string): Promise<any> => {
    const queryParams = period ? `?period=${period}` : '';
    const response = await fetch(`${API_BASE_URL}/creators/stripe-revenue/${creatorId}${queryParams}`);
    if (!response.ok) {
      throw new Error('Failed to get Stripe revenue data');
    }
    return response.json();
  },

  getStripeSubscriptions: async (creatorId: string): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/creators/stripe-subscriptions/${creatorId}`);
    if (!response.ok) {
      throw new Error('Failed to get Stripe subscriptions');
    }
    return response.json();
  },

  getStripePayouts: async (creatorId: string, limit = 50): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/creators/stripe-payouts/${creatorId}?limit=${limit}`);
    if (!response.ok) {
      throw new Error('Failed to get Stripe payouts');
    }
    return response.json();
  },

  syncStripeStatus: async (creatorId: string): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/creators/sync-stripe-status/${creatorId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      throw new Error('Failed to sync Stripe status');
    }
    return response.json();
  },

  // Test connection
  testConnection: async (): Promise<{ success: boolean; time: string }> => {
    const response = await fetch(`${API_BASE_URL}/test`);
    return response.json();
  },

  // Users
  getUsers: async (): Promise<User[]> => {
    const response = await fetch(`${API_BASE_URL}/users`);
    return response.json();
  },

  getCreators: async (): Promise<User[]> => {
    try {
      const response = await fetchWithRetry(`${API_BASE_URL}/creators`);
      if (!response.ok) {
        console.error(`Failed to fetch creators: ${response.status} ${response.statusText}`);
        return [];
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching creators:', error);
      return [];
    }
  },

  // Paginated version with search and filtering
  getCreatorsPaginated: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    sports?: string[];
    sortBy?: 'name' | 'price-low' | 'price-high' | 'winRate';
  } = {}): Promise<{
    creators: User[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  }> => {
    try {
      const { page = 1, limit = 20, search, sports, sortBy } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search) queryParams.append('search', search);
      if (sports && sports.length > 0) queryParams.append('sports', sports.join(','));
      if (sortBy) queryParams.append('sortBy', sortBy);

      const response = await fetch(`${API_BASE_URL}/creators/paginated?${queryParams}`);

      if (!response.ok) {
        // Fallback to regular getCreators if pagination endpoint doesn't exist
        const creators = await api.getCreators();
        return {
          creators: creators.slice((page - 1) * limit, page * limit),
          total: creators.length,
          page,
          limit,
          hasMore: page * limit < creators.length
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching paginated creators:', error);

      // Fallback to regular getCreators with client-side pagination
      try {
        const creators = await api.getCreators();
        const { page = 1, limit = 20, search, sports, sortBy } = params;

        let filteredCreators = [...creators];

        // Client-side search
        if (search) {
          const searchLower = search.toLowerCase();
          filteredCreators = filteredCreators.filter(creator =>
            creator.username?.toLowerCase().includes(searchLower) ||
            creator.name.toLowerCase().includes(searchLower) ||
            creator.bio?.toLowerCase().includes(searchLower)
          );
        }

        // Client-side sport filtering
        if (sports && sports.length > 0) {
          filteredCreators = filteredCreators.filter(creator =>
            sports.some(sport => creator.specialty_sport?.includes(sport))
          );
        }

        // Client-side sorting
        if (sortBy) {
          filteredCreators.sort((a, b) => {
            switch (sortBy) {
              case 'name':
                return a.name.localeCompare(b.name);
              case 'price-low':
                return (a.price || 0) - (b.price || 0);
              case 'price-high':
                return (b.price || 0) - (a.price || 0);
              default:
                return 0;
            }
          });
        }

        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;

        return {
          creators: filteredCreators.slice(startIndex, endIndex),
          total: filteredCreators.length,
          page,
          limit,
          hasMore: endIndex < filteredCreators.length
        };
      } catch (fallbackError) {
        return {
          creators: [],
          total: 0,
          page: 1,
          limit: 20,
          hasMore: false
        };
      }
    }
  },

  createUser: async (userData: { name: string; email: string; phone?: string }): Promise<User> => {
    const response = await fetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    return response.json();
  },

  // Clients
  getClients: async (): Promise<Client[]> => {
    const response = await fetch(`${API_BASE_URL}/clients`);
    return response.json();
  },

  createClient: async (clientData: { name: string; contact_email: string }): Promise<Client> => {
    const response = await fetch(`${API_BASE_URL}/clients`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(clientData),
    });
    return response.json();
  },

  // Picks
  getPicks: async (): Promise<Pick[]> => {
    const response = await fetch(`${API_BASE_URL}/picks`);
    return response.json();
  },

  getPicksByUser: async (userId: string): Promise<Pick[]> => {
    const response = await fetch(`${API_BASE_URL}/picks/user/${userId}`);
    return response.json();
  },

  getPicksFromSubscribedCreators: async (userId: string): Promise<any[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/picks/subscribed/${userId}`);
      if (!response.ok) {
        return [];
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return [];
    }
  },

  getUserByEmail: async (email: string): Promise<User | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/email/${email}`);
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return null;
    }
  },

  syncOAuthUser: async (userData: {
    email: string;
    name?: string;
    auth_id: string;
    provider?: string;
    phone?: string;
  }): Promise<{ user: User; created: boolean } | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/sync-oauth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to sync OAuth user:', errorData);
        return null;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error syncing OAuth user:', error);
      return null;
    }
  },

  createPick: async (pickData: { user_id: string; client_id: string; pick_details: string }): Promise<Pick> => {
    const response = await fetch(`${API_BASE_URL}/picks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(pickData),
    });
    return response.json();
  },









  // Purchases
  getUserPurchases: async (userId: string): Promise<Purchase[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/purchases/user/${userId}`);
      if (!response.ok) {
        return [];
      }
      const data = await response.json();
      return data;
    } catch (error) {
      return [];
    }
  },

  verifyPurchase: async (userId: string, creatorId: string): Promise<{ exists: boolean; purchase?: any }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/purchases/verify?userId=${userId}&creatorId=${creatorId}`);
      if (!response.ok) {
        return { exists: false };
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error verifying purchase:', error);
      return { exists: false };
    }
  },

  getCreatorPurchases: async (creatorId: string): Promise<Purchase[]> => {
    const response = await fetch(`${API_BASE_URL}/purchases/creator/${creatorId}`);
    return response.json();
  },

  getCreatorAnalytics: async (creatorId: string, timeframe: '7d' | '30d' | '90d' | 'all' = 'all'): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/creator/analytics/${creatorId}?timeframe=${timeframe}`);
    if (!response.ok) {
      throw new Error('Failed to fetch creator analytics');
    }
    return response.json();
  },

  getCreatorStats: async (creatorId: string): Promise<{
    totalPicks: number;
    activeSubscribers: number;
    lastActive: string | null;
    isAmbassador: boolean;
    isActive: boolean;
  }> => {
    const response = await fetch(`${API_BASE_URL}/creators/${creatorId}/stats`);
    return response.json();
  },

  createPurchase: async (purchaseData: { user_id: string; creator_id: string; amount?: number; subscription_type?: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'free' }): Promise<Purchase> => {
    const response = await fetch(`${API_BASE_URL}/purchases`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(purchaseData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw errorData;
    }
    
    return response.json();
  },

  cancelPurchase: async (purchaseId: string, cancelImmediately: boolean = false): Promise<Purchase> => {
    const url = cancelImmediately
      ? `${API_BASE_URL}/purchases/${purchaseId}?cancelImmediately=true`
      : `${API_BASE_URL}/purchases/${purchaseId}`;
    const response = await fetch(url, {
      method: 'DELETE',
    });
    return response.json();
  },

  // Subscription Management
  getSubscriptionDetails: async (userId: string): Promise<any[]> => {
    const response = await fetch(`${API_BASE_URL}/subscriptions/manage/${userId}`);
    return response.json();
  },

  updateAutoRenewal: async (purchaseId: string, autoRenewal: boolean): Promise<Purchase> => {
    const response = await fetch(`${API_BASE_URL}/subscriptions/${purchaseId}/auto-renewal`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ auto_renewal: autoRenewal }),
    });
    return response.json();
  },

  // SMS Notifications
  subscribeToSMS: async (data: { user_id: string; creator_id: string; phone_number: string }): Promise<SMSNotification> => {
    const response = await fetch(`${API_BASE_URL}/sms/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  unsubscribeFromSMS: async (data: { user_id: string; creator_id: string }): Promise<SMSNotification> => {
    const response = await fetch(`${API_BASE_URL}/sms/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  getSMSStatus: async (userId: string): Promise<Array<{ creator_id: string; phone_number: string; is_active: boolean }>> => {
    const response = await fetch(`${API_BASE_URL}/sms/status/${userId}`);
    if (!response.ok) return [];
    return response.json();
  },

  getSMSPreferences: async (userId: string): Promise<SMSNotification[]> => {
    const response = await fetch(`${API_BASE_URL}/sms/preferences/${userId}`);
    return response.json();
  },

  updateSMSPreference: async (notificationId: string, data: { is_active: boolean; phone_number: string }): Promise<SMSNotification> => {
    const response = await fetch(`${API_BASE_URL}/sms/preferences/${notificationId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  getSMSLogs: async (userId: string): Promise<SMSLog[]> => {
    const response = await fetch(`${API_BASE_URL}/sms/logs/${userId}`);
    return response.json();
  },

  getCreatorSMSStats: async (creatorId: string) => {
    const response = await fetch(`${API_BASE_URL}/sms/creator-stats/${creatorId}`);
    return response.json();
  },

  getCreatorSMSLogs: async (creatorId: string, limit?: number, includePending?: boolean) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (includePending) params.append('includePending', 'true');

    const url = `${API_BASE_URL}/sms/creator-logs/${creatorId}${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url);
    return response.json();
  },

  sendSMSBatchNow: async (batchId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/sms/send-batch-now/${batchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('Send batch now error:', text);
        return { success: false, error: 'Failed to send batch' };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error sending batch now:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to send batch' };
    }
  },

  // Subscription Pricing
  getSubscriptionPricing: async (creatorId: string): Promise<SubscriptionPricing> => {
    const response = await fetch(`${API_BASE_URL}/subscription-pricing/${creatorId}`);
    return response.json();
  },

  createSubscriptionPricing: async (data: { creator_id: string; daily_price?: number | null; weekly_price?: number | null; monthly_price?: number | null; yearly_price?: number | null; daily_description?: string | null; weekly_description?: string | null; monthly_description?: string | null; yearly_description?: string | null; billing_frequency?: string; is_free?: boolean }): Promise<SubscriptionPricing> => {
    const response = await fetch(`${API_BASE_URL}/subscription-pricing`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create pricing: ${response.status} - ${errorText}`);
    }

    return response.json();
  },

  updateSubscriptionPricing: async (creatorId: string, data: { daily_price?: number | null; weekly_price?: number | null; monthly_price?: number | null; yearly_price?: number | null; daily_description?: string | null; weekly_description?: string | null; monthly_description?: string | null; yearly_description?: string | null; billing_frequency?: string; is_active?: boolean; is_free?: boolean }): Promise<SubscriptionPricing> => {
    const response = await fetch(`${API_BASE_URL}/subscription-pricing/${creatorId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update pricing: ${response.status} - ${errorText}`);
    }

    return response.json();
  },

  // Delivery Methods
  getDeliveryMethods: async (creatorId: string): Promise<DeliveryMethod[]> => {
    const response = await fetch(`${API_BASE_URL}/delivery-methods/${creatorId}`);
    return response.json();
  },

  createDeliveryMethod: async (data: { creator_id: string; method_type: string; method_name: string; method_value: string }): Promise<DeliveryMethod> => {
    const response = await fetch(`${API_BASE_URL}/delivery-methods`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateDeliveryMethod: async (methodId: string, data: { method_name: string; method_value: string; is_active: boolean }): Promise<DeliveryMethod> => {
    const response = await fetch(`${API_BASE_URL}/delivery-methods/${methodId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteDeliveryMethod: async (methodId: string): Promise<DeliveryMethod> => {
    const response = await fetch(`${API_BASE_URL}/delivery-methods/${methodId}`, {
      method: 'DELETE',
    });
    return response.json();
  },


  // Admin endpoints
  getAllPurchases: async (): Promise<Purchase[]> => {
    const response = await fetch(`${API_BASE_URL}/purchases`);
    return response.json();
  },

  // Deposits
  getAllDeposits: async (params?: { page?: number; limit?: number; status?: string; userId?: string }): Promise<{ deposits: Deposit[]; pagination: any }> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.status) searchParams.append('status', params.status);
    if (params?.userId) searchParams.append('userId', params.userId);
    
    const response = await fetch(`${API_BASE_URL}/deposits?${searchParams}`);
    return response.json();
  },

  getDepositStats: async (): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/deposits/stats`);
    return response.json();
  },

  // Games API for step-by-step pick creation
  getGames: async (sport: string, date: string): Promise<{ games: any[] }> => {
    const response = await fetch(`${API_BASE_URL}/games/${sport}/${date}`);
    return response.json();
  },

  // Fetch ALL games for a sport (no date filtering)
  getAllGames: async (sport: string): Promise<{ games: any[] }> => {
    const response = await fetch(`${API_BASE_URL}/games/${sport}/all`);
    return response.json();
  },

  getEventOdds: async (eventId: string): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/events/${eventId}/odds`);
    return response.json();
  },

  // Fetch player props for any sport/event
  fetchPlayerProps: async (sport: string, eventId: string, markets?: string) => {
    let url = `${API_BASE_URL}/odds/event/${sport}/${eventId}/player-props`;
    if (markets) url += `?markets=${markets}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch player props');
    return response.json();
  },

  // Get available sports from Odds API
  getAvailableSports: async (): Promise<any[]> => {
    const response = await fetch(`${API_BASE_URL}/odds-api/sports`);
    if (!response.ok) throw new Error('Failed to fetch sports');
    return response.json();
  },

  // Get odds for a specific game
  getGameOdds: async (sport: string, gameId: string): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/odds/${sport}/${gameId}`);
    if (!response.ok) throw new Error('Failed to fetch game odds');
    return response.json();
  },

  // Enhanced pick creation with comprehensive data structure
  createEnhancedPick: async (pickData: {
    user_id: string;
    client_id: string;
    sport: string;
    game_date: string;
    home_team: string;
    away_team: string;
    bet_type: string;
    pick: string;
    odds: string;
    reasoning: string;
    game_id?: string;
    bookmaker?: string;
    line?: number;
    player?: string;
    stat?: string;
    overUnder?: string;
  }): Promise<any> => {
    const enhancedPickDetails = {
      sport: pickData.sport,
      game_date: pickData.game_date,
      home_team: pickData.home_team,
      away_team: pickData.away_team,
      bet_type: pickData.bet_type,
      pick: pickData.pick,
      odds: pickData.odds,
      reasoning: pickData.reasoning,
      result: 'pending',
      created_at: new Date().toISOString(),
      game_id: pickData.game_id,
      bookmaker: pickData.bookmaker,
      
      // Additional grading data
      ...(pickData.line && { line: pickData.line }),
      ...(pickData.player && { player: pickData.player }),
      ...(pickData.stat && { stat: pickData.stat }),
      ...(pickData.overUnder && { overUnder: pickData.overUnder }),
      
      // Metadata for tracking
      api_version: '2.0',
      form_type: 'optimized'
    };

    const response = await fetch(`${API_BASE_URL}/picks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: pickData.user_id,
        client_id: pickData.client_id,
        pick_details: JSON.stringify(enhancedPickDetails)
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw errorData;
    }

    return response.json();
  },

  // Get bookmaker rankings for best odds highlighting
  getBookmakerRankings: async (): Promise<{ [key: string]: number }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookmaker-rankings`);
      if (!response.ok) return {};
      return response.json();
    } catch {
      // Default rankings if endpoint doesn't exist
      return {
        'FanDuel': 1,
        'DraftKings': 2,
        'BetMGM': 3,
        'Caesars': 4,
        'PointsBet': 5,
        'Barstool': 6
      };
    }
  },

  // Validate pick data before submission
  validatePickData: async (pickData: any): Promise<{ valid: boolean; errors: string[] }> => {
    const errors: string[] = [];

    if (!pickData.sport) errors.push('Sport is required');
    if (!pickData.game_date) errors.push('Game date is required');
    if (!pickData.home_team || !pickData.away_team) errors.push('Both teams are required');
    if (!pickData.bet_type) errors.push('Bet type is required');
    if (!pickData.pick) errors.push('Pick selection is required');
    if (!pickData.odds) errors.push('Odds are required');
    if (!pickData.reasoning || pickData.reasoning.trim().length < 10) {
      errors.push('Reasoning must be at least 10 characters');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  },

  async getShareLink(creatorId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/creators/${creatorId}/share-link`);
    if (!response.ok) throw new Error('Failed to get share link');
    return response.json();
  },

  // Email notification preferences (per-creator)
  getAllEmailPreferences: async (userId: string): Promise<Array<{creator_id: string, enabled: boolean}>> => {
    const response = await fetch(`${API_BASE_URL}/email-preferences/${userId}/all`);
    if (!response.ok) {
      throw new Error('Failed to get all email preferences');
    }
    return response.json();
  },

  getEmailPreferences: async (userId: string, creatorId: string): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/email-preferences/${userId}/creator/${creatorId}`);
    if (!response.ok) {
      throw new Error('Failed to get email preferences');
    }
    return response.json();
  },

  updateEmailPreferences: async (userId: string, creatorId: string, enabled: boolean): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/email-preferences/${userId}/creator/${creatorId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ enabled }),
    });
    if (!response.ok) {
      throw new Error('Failed to update email preferences');
    }
    return response.json();
  },

  // Get email notification statistics for creators
  getCreatorEmailStats: async (creatorId: string, timeframe: string = '30d'): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/creator/email-stats/${creatorId}?timeframe=${timeframe}`);
    if (!response.ok) {
      throw new Error('Failed to get email statistics');
    }
    return response.json();
  },

  // Send test email (for development/testing)
  sendTestEmail: async (email: string): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/test-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      throw new Error('Failed to send test email');
    }
    return response.json();
  },

  // Get all subscribers for a creator with their notification preferences
  getCreatorSubscribers: async (creatorId: string): Promise<{
    success: boolean;
    subscribers: SubscriberWithNotifications[];
    total: number;
  }> => {
    const response = await fetch(`${API_BASE_URL}/creator/subscribers/${creatorId}`);
    if (!response.ok) {
      throw new Error('Failed to get creator subscribers');
    }
    return response.json();
  },

  // Update notification preferences for a specific subscriber
  updateSubscriberNotifications: async (
    userId: string,
    creatorId: string,
    preferences: { smsEnabled?: boolean; emailEnabled?: boolean }
  ): Promise<{
    success: boolean;
    message: string;
    preferences: { sms_enabled: boolean; email_enabled: boolean };
  }> => {
    const response = await fetch(`${API_BASE_URL}/creator/subscribers/${userId}/notifications`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        creatorId,
        smsEnabled: preferences.smsEnabled,
        emailEnabled: preferences.emailEnabled,
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to update subscriber notification preferences');
    }
    return response.json();
  },

  // Get creator by ID
  getCreatorById: async (creatorId: string): Promise<User | null> => {
    try {
      const creators = await api.getCreators();
      return creators.find(c => c.id === creatorId) || null;
    } catch (error) {
      console.error('Error fetching creator:', error);
      return null;
    }
  },

  // Get user by ID
  getUserById: async (userId: string): Promise<User | null> => {
    try {
      const users = await api.getUsers();
      return users.find(u => u.id === userId) || null;
    } catch (error) {
      console.error('Error fetching user:', error);
      return null;
    }
  },

  // ===========================
  // ADMIN ENDPOINTS
  // ===========================

  // Set admin password (one-time setup)
  setAdminPassword: async (password: string): Promise<{ success: boolean; message: string }> => {
    // Get Supabase session token
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/set-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ password }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Verify admin password
  verifyAdminPassword: async (password: string): Promise<{
    success: boolean;
    adminSession: {
      userId: string;
      email: string;
      expiresAt: string;
    };
  }> => {
    // Get Supabase session token
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/verify-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ password }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin dashboard data
  getAdminDashboard: async (): Promise<any> => {
    // Get Supabase session token
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/dashboard`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin metrics overview
  getAdminMetricsOverview: async (): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/metrics/overview`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin revenue analytics
  getAdminMetricsRevenue: async (): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/metrics/revenue`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get webhook health status
  getAdminWebhookHealth: async (): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/webhook-health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin delivery status
  getAdminDeliveryStatus: async (limit: number = 50): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/delivery/status?limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin delivery verification for specific pick
  getAdminDeliveryVerify: async (pickId: string): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/delivery/verify/${pickId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin subscriptions overview
  getAdminSubscriptionsOverview: async (): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/subscriptions/overview`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin creators performance
  getAdminCreatorsPerformance: async (limit: number = 20): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/creators/performance?limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin purchases with pagination
  getAdminPurchases: async (page: number = 1, sortBy?: string, sortOrder?: string, status?: string, searchCustomer?: string, searchCreator?: string): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const queryParams = new URLSearchParams({ page: page.toString() });
    if (sortBy) queryParams.append('sortBy', sortBy);
    if (sortOrder) queryParams.append('sortOrder', sortOrder);
    if (status) queryParams.append('status', status);
    if (searchCustomer) queryParams.append('searchCustomer', searchCustomer);
    if (searchCreator) queryParams.append('searchCreator', searchCreator);

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/purchases?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get purchase delivery details
  getPurchaseDeliveryDetails: async (purchaseId: string): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/purchases/${purchaseId}/delivery-details`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin email logs with filters
  getAdminEmailLogs: async (page: number = 1, status?: string, search?: string, sortBy?: string, sortOrder?: string, includePersonal?: boolean, emailType?: string): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const queryParams = new URLSearchParams({ page: page.toString() });
    if (status) queryParams.append('status', status);
    if (search) queryParams.append('search', search);
    if (sortBy) queryParams.append('sortBy', sortBy);
    if (sortOrder) queryParams.append('sortOrder', sortOrder);
    if (includePersonal !== undefined) queryParams.append('includePersonal', includePersonal.toString());
    if (emailType) queryParams.append('emailType', emailType);

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/email-logs?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin email content detail
  getAdminEmailContent: async (emailId: string): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/email-logs/${emailId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get system status for all services
  getSystemStatus: async (): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/system-status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin creators with pagination and sorting
  getAdminCreators: async (page: number = 1, sortBy: string = 'created_at', sortOrder: string = 'desc'): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/creators?page=${page}&sortBy=${sortBy}&sortOrder=${sortOrder}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get subscribers for a specific creator
  getAdminCreatorSubscribers: async (creatorId: string): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/creators/${creatorId}/subscribers`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  getAdminCustomers: async (page: number = 1, sortBy: string = 'total_spent', sortOrder: string = 'desc', search: string = ''): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    let url = `${API_BASE_URL}/admin/customers?page=${page}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    const response = await fetchWithRetry(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin SMS logs with filters
  getAdminSMSLogs: async (page: number = 1, status?: string, search?: string, sortBy?: string, sortOrder?: string, includePersonal?: boolean): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const queryParams = new URLSearchParams({ page: page.toString() });
    if (status) queryParams.append('status', status);
    if (search) queryParams.append('search', search);
    if (sortBy) queryParams.append('sortBy', sortBy);
    if (sortOrder) queryParams.append('sortOrder', sortOrder);
    if (includePersonal !== undefined) queryParams.append('includePersonal', includePersonal.toString());

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/sms-logs?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin SMS content detail
  getAdminSMSContent: async (smsId: string): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/sms-logs/${smsId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin SMS subscribers with filters
  getAdminSMSSubscribers: async (page: number = 1, status?: string, search?: string, sortBy?: string, sortOrder?: string): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const queryParams = new URLSearchParams({ page: page.toString() });
    if (status) queryParams.append('status', status);
    if (search) queryParams.append('search', search);
    if (sortBy) queryParams.append('sortBy', sortBy);
    if (sortOrder) queryParams.append('sortOrder', sortOrder);

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/sms-subscribers?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  },

  // Get admin email subscribers with filters
  getAdminEmailSubscribers: async (page: number = 1, status?: string, search?: string, sortBy?: string, sortOrder?: string): Promise<any> => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const queryParams = new URLSearchParams({ page: page.toString() });
    if (status) queryParams.append('status', status);
    if (search) queryParams.append('search', search);
    if (sortBy) queryParams.append('sortBy', sortBy);
    if (sortOrder) queryParams.append('sortOrder', sortOrder);

    const response = await fetchWithRetry(`${API_BASE_URL}/admin/email-subscribers?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error, status: response.status } };
    }
    return response.json();
  }

}; 